import moment from 'moment';

import { colors } from '@styleseat/brand';
import { getRecommendationsData } from '../../../../../modules/getRecommendationsData';
import stateManager from '../../../../../modules/user/stateManager';
import analytics from '../../../../../modules/analytics';
import nonCriticalException from '../../../../../modules/exceptionLogger';

import { InsightCardProps, InsightCardFactoryProps } from '../../Components/InsightCards/types';

import { CARD_TYPES } from '../../Components/InsightCards/cards/InsightCardFactory';
import {
  LAST_ACTION_DATE,
  INSIGHT_ACTION_PREFIX,
  MARKETING_TIPS_ACTIONS,
  ACTION_COUNT,
  INSIGHT_TYPES,
} from '../../../MarketingUtils/constants';
import { InsightModel } from '../types';
import { PRO_IMAGE_LIST } from '../../../../../route-names';
import { MARKETING_TIP_SOURCE } from '../routing';

export const CARD_BASE_ID = 'tag-photos';

export const ACTION_PREFIX = `${INSIGHT_ACTION_PREFIX}${CARD_BASE_ID}-`;
export const DATE_ACTION_LAST_TAKEN = 'add-photos-insight-date-action-last-taken';

const S3_NAMESPACE = 'FLY-2910';
const ONE_WEEK_IN_SECONDS = 7 * 24 * 60 * 60;

/**
 * parseTagPhotosCardData - parse data it into InsightCardProps
 * and return the results
 *
 * @param serviceData: { id, name } - the name and id of the service to tag
 * @param providerId
 * @returns : MarqueeCarProps
 */
export const parseCardData = ({
  id, name, updateInsights,
}, providerId, cardIndex):
InsightCardProps => ({
  id,
  headline: 'Bring in new clients when you add photos of',
  subhead: name,
  subheadColor: colors.slate,
  bodyText: [
    {
      text: `Adding photos of ${name}`,
      color: colors.slate,
    },
    {
      text: 'will drive the most new clients to you in your area',
    },
  ],
  cta: 'add photos',
  clickThroughData: {
    route: PRO_IMAGE_LIST,
    params: {
      providerId,
      edit: 1,
      source: MARKETING_TIP_SOURCE,
      serviceId: id,
    },
  },
  analyticsData: {
    event: 'pro_home_missing_photo_grow_click',
    payload: {
      providerId,
      serviceId: id,
      card_index: cardIndex,
    },
  },
  firstViewTracking: () => analytics.track('pro_home_missing_photo_grow_view', { card_index: cardIndex }),
  onAction: async () => {
    const actions = {
      [LAST_ACTION_DATE]: moment().toISOString(),
      [DATE_ACTION_LAST_TAKEN]: +Date.now(),
    };
    stateManager.addActions(actions);
  },
  dismissParams: {
    analyticsDismissData: {
      event: 'pro_home_missing_photo_grow_dismiss',
      payload: {
        providerId,
        serviceId: id,
      },
    },
    onDismiss: async () => {
      const count = await stateManager.getActionValue(ACTION_COUNT);
      const newCount = count ? Math.min(3, count + 1) : 1;
      const actions = {
        [ACTION_COUNT]: newCount,
        [`${ACTION_PREFIX}${count || 0}`]: true,
        // NOTE: +new Date() converts the date to epoch seconds.
        [DATE_ACTION_LAST_TAKEN]: +Date.now(),
      };
      await stateManager.addActions(actions);
      updateInsights({ type: MARKETING_TIPS_ACTIONS.dismiss, cardId: id });
    },
    dismissText: 'No thanks',
  },
  timeToComplete: '2 minutes',
});

export const getInsights = async (providerId): Promise<InsightModel[]> => {
  try {
    // Get Insights
    const insights: {} = await getRecommendationsData(S3_NAMESPACE, providerId);
    if (Object.keys(insights).length === 0) {
      return [];
    }

    // Check state and verify we should resurface or not based on when the last
    // action was taken.
    const lastTaken = await stateManager.getActionValue(`${DATE_ACTION_LAST_TAKEN}`) ?? 0;
    // NOTE: +new Date() converts the date to epoch seconds.
    const weeksLastTaken = (+new Date() - lastTaken) / ONE_WEEK_IN_SECONDS;
    if (weeksLastTaken < 1) {
      return [];
    }

    // Display the first insight
    const firstInsight = insights[Object.keys(insights)[0]];
    const insightCard = {
      insight_type: INSIGHT_TYPES.AddPhotos,
      id: firstInsight.service_id,
      name: firstInsight.service_name,
    };
    return [insightCard];
  } catch (error) {
    nonCriticalException(error);

    return [];
  }
};

export const parseAddPhotosInsight = (provider, insight, updateInsights, user, metrics, cardIndex):
InsightCardFactoryProps => ({
  type: CARD_TYPES.inlineHeadline,
  cardProps: parseCardData(
    {
      id: insight.id, name: insight.name, updateInsights,
    }, provider.id, cardIndex,
  ),
});
